import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import { ChatBubbleOvalLeftIcon } from "@heroicons/react/24/outline";
import Chat from "./Chat";
import MeetingScheduler from "../components/MeetingScheduler";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import axiosInstance from "../utils/axiosInstance";

const Profile = () => {
  const [activeChatUserId, setActiveChatUserId] = useState(null);
  const [activeChatUsername, setActiveChatUsername] = useState(null);
  const { userId, token } = useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [schedulerFor, setSchedulerFor] = useState(null);

  const handleRemoveMatch = async (matchId) => {
    if (!window.confirm("Are you sure you want to remove this match?")) return;

    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE}/api/users/remove-match`,
        { userId, matchId },
        { headers: { Authorization: token } }
      );
      const res = await axios.get(`${process.env.REACT_APP_API_BASE}/api/users/${userId}`, {
        headers: { Authorization: token }
      });
      setUser(res.data);
    } catch (err) {
      console.error("❌ Failed to remove match", err);
      alert("Failed to remove match");
    }
  };

  useEffect(() => {
    if (!userId) return;

    const fetchUser = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE}/api/users/${userId}`, {
          headers: { Authorization: token },
        });
        setUser(res.data);
      } catch (err) {
        console.error("Error loading profile", err);
      }
    };

    fetchUser();
  }, [userId, token]);

  const togglePairLinged = async () => {
    try {
      const newStatus = !user.isPairLinged;
      await axiosInstance.post(`${process.env.REACT_APP_API_BASE}/api/users/pairlinged`,
        { userId, isPairLinged: newStatus },
        { headers: { Authorization: token } });
      setUser((prev) => ({ ...prev, isPairLinged: newStatus }));
      toast.success(`Status updated to ${newStatus ? 'PairLinged' : 'Available'}`);
    } catch (err) {
      console.error("Error updating status", err);
      toast.error("Failed to update status");
    }
  };

  if (!user) return <div className="p-4">Loading profile...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-indigo-200 px-4 py-6">
      <Helmet>
        <title>Profile - PairLingo</title>
        <meta name="description" content="Speak with perfect language partner on PairLingo." />
        <meta name="keywords" content="language learning, pairlingo, chat, match, speaking chat, german language partner chat, language speaking practice" />
      </Helmet>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-md rounded-lg p-6 mb-6 text-center">
          <h1 className="text-2xl font-bold text-indigo-700">👋 Hi, {user.username}!</h1>
          <p className="text-gray-600 mt-2 text-sm">
            You're learning <span className="font-semibold">{user.targetLanguage}</span> at level{" "}
            <span className="font-semibold">{user.level}</span>
          </p>
          <div className="flex justify-center items-center gap-2 mt-2">
            <button
              onClick={togglePairLinged}
              className={`text-sm px-4 py-2 rounded ${user.isPairLinged ? "bg-gray-400 text-white" : "bg-indigo-600 text-white"} hover:bg-indigo-500`}
            >
              {user.isPairLinged ? "PairLinged" : "Set PairLinged"}
            </button>
            <div className="relative group">
              <span className="text-gray-400 cursor-pointer">❔</span>
              <div className="absolute hidden group-hover:block bg-white border border-gray-300 text-sm p-2 rounded shadow-lg">
                Mark yourself as occupied
              </div>
            </div>
          </div>
          {/* Upcoming Meetings Section */}
          <div className="mt-4 bg-white p-4 rounded shadow text-center">
            <h2 className="text-lg font-semibold text-indigo-700 mb-2">📅 Your Upcoming Meetings</h2>
            <Link to="/meetings" className="text-sm text-indigo-600 hover:underline">
              View all scheduled meetings →
            </Link>
          </div>
        </div>
        {/* Matches */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-indigo-700">💬 Your Matches</h2>
          {user.matches.length === 0 ? (
            <p className="text-gray-500 italic text-sm">
              😕 You don't have any matches yet. Try exploring users and send a match request!
            </p>
          ) : (
            <div className="space-y-4">
              {user.matches.map((match) => (
                <div
                  key={match._id}
                  className="w-full bg-white rounded-xl shadow-md p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between hover:shadow-lg transition-all"
                >
                  {/* LEFT: Avatar + Info */}
                  <div className="flex items-center gap-4 mb-4 sm:mb-0">
                    <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center text-xl font-bold text-indigo-600">
                      {match.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-indigo-800">{match.username}</h2>
                      <p className="text-sm text-gray-600">
                        Learning: <span className="font-medium">{match.targetLanguage}</span> • Level:{" "}
                        <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">
                          {match.level}
                        </span>
                      </p>
                      <p className="text-sm italic text-gray-500 mt-1">"{match.bio}"</p>
                    </div>
                  </div>

                  {/* RIGHT: Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:ml-auto">
                    <button
                      onClick={() =>
                        schedulerFor === match._id ? setSchedulerFor(null) : setSchedulerFor(match._id)
                      }
                      className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200"
                    >
                      📅 Schedule
                    </button>
                    <button
                      onClick={() => {
                        setActiveChatUserId(match._id);
                        setActiveChatUsername(match.username);
                      }}
                      className="text-sm bg-indigo-100 text-indigo-700 px-3 py-1 rounded hover:bg-indigo-200 flex items-center justify-center"
                    >
                      <ChatBubbleOvalLeftIcon className="w-4 h-4 mr-1" />
                      Chat
                    </button>
                    <button
                      onClick={() => handleRemoveMatch(match._id)}
                      className="text-sm bg-red-100 text-red-600 px-3 py-1 rounded hover:bg-red-200"
                    >
                      ❌ Remove
                    </button>
                  </div>
                </div>
              ))}
              {schedulerFor && (
                <div className="mt-4">
                  <MeetingScheduler
                    receiverId={schedulerFor}
                    onClose={() => setSchedulerFor(null)}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chat panel */}
        {activeChatUserId && (
          <div className="fixed top-0 right-0 h-full w-full sm:w-[400px] bg-white shadow-2xl border-l z-50 flex flex-col animate-slide-in">
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <img
                  src={`https://ui-avatars.com/api/?name=${activeChatUsername}&background=random`}
                  alt={activeChatUsername}
                  className="w-8 h-8 rounded-full"
                />
                <div>
                  <div className="text-sm font-semibold text-indigo-700">{activeChatUsername}</div>
                  <div className="text-xs text-green-600">🟢 Online</div>
                </div>
              </div>
              <button
                onClick={() => {
                  setActiveChatUserId(null);
                  setActiveChatUsername(null);
                }}
                className="text-gray-500 hover:text-red-500 transition"
                title="Close"
              >
                ✖
              </button>
            </div>
            <div className="flex-1 p-2 overflow-y-auto">
              <Chat receiverId={activeChatUserId} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
